import React, { useEffect, useState } from 'react';
import { Footer, Navbar } from './component';
import Cookies from 'universal-cookie';
import axios from 'axios';
import { LoginUrl, gmailLogin, SignUpUrl } from './serverurl';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet } from 'react-helmet-async';

function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [processing, set_processing] = useState(false);
  const [disabled_status, set_disabled_status] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [fullname, setFullname] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const cookies = new Cookies();
  const navigate = useNavigate();

  const validateForm = () => {
    const errors = {};
    if (!fullname.trim()) errors.fullname = 'Full name is required';
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Email is invalid';
    if (!password) errors.password = 'Password is required';
    if (password && password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (confirmPassword !== password) errors.confirmPassword = 'Passwords do not match';
    return errors;
  };

  const registerUser = (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    set_processing(true);
    set_disabled_status(true);

    const data = {
      name: fullname,
      email,
      password,
      confirm_password: confirmPassword,
    };

    axios.post(SignUpUrl, data)
      .then((response) => {
        if (response.data.details) {
          toast.success(response.data.details);
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else if (response.data.message) {
          toast.success(response.data.message);
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      })
      .catch((error) => {
        console.log('Registration error:', error);
        if (error.response && error.response.data.errors) {
          error.response.data.errors.forEach(err => {
            toast.error(err.msg);
            if (err.field) {
              setFormErrors(prev => ({
                ...prev,
                [err.field]: err.msg
              }));
            }
          });
        } else if (error.response && error.response.data.message) {
          toast.error(error.response.data.message);
        } else if (error.response && error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            toast.error(error.response.data.detail);
          } else if (error.response.data.detail.errors) {
            error.response.data.detail.errors.forEach(err => toast.error(err.msg));
          }
        } else {
          toast.error('Registration failed. Please try again.');
        }
      })
      .finally(() => {
        set_processing(false);
        set_disabled_status(false);
      });
  };

  // Updated Google login response handler
  const responseMessage = (response) => {
    try {
      jwtDecode(response.credential); // optional validation
      set_processing(true);
      set_disabled_status(true);

      const data = {
        token: response.credential,
        browser: true,
        user_type: 'cbt_creator' // indicates creator sign-up/login
      };

      axios.post(gmailLogin, data)
        .then((res) => {
          // Successful response contains access_token, auto_login_code, name, email, etc.
          if (res.status === 200 && res.data.access_token) {
            const expiryDate = new Date();
            expiryDate.setDate(expiryDate.getDate() + 7);

            // Set cookies exactly as in login component
            cookies.set('auto_login_code', res.data.auto_login_code, { expires: expiryDate });
            cookies.set('auth_key', res.data.access_token, { expires: expiryDate });
            cookies.set('access_token', res.data.access_token, { expires: expiryDate });
            cookies.set('name', res.data.name, { expires: expiryDate });
            cookies.set('email', res.data.email, { expires: expiryDate });
            // Optional fields
            if (res.data.surname) {
              cookies.set('surname', res.data.surname, { expires: expiryDate });
            }
            cookies.set('authenticated', true, { expires: expiryDate });
            cookies.set('user_type', 'creator', { expires: expiryDate });

            toast.success('Google sign-up successful!');
            navigate('/cbt_creator/dashboard');
          } else {
            // Handle unexpected response
            toast.error(res.data?.message || 'Authentication failed');
          }
        })
        .catch((error) => {
          console.error('Google sign-up error:', error);
          if (error.response && error.response.data.errors) {
            error.response.data.errors.forEach(err => toast.error(err.msg));
          } else if (error.response && error.response.data.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error('Google sign-up failed. Please try again.');
          }
        })
        .finally(() => {
          set_processing(false);
          set_disabled_status(false);
        });
    } catch (error) {
      toast.error('Failed to process Google sign-up. Please try again.');
      console.error('JWT decode error:', error);
      set_processing(false);
      set_disabled_status(false);
    }
  };

  const errorMessage = (error) => {
    toast.error('Google sign-up failed. Please try another method.');
    console.error('Google auth error:', error);
  };

  useEffect(() => {
    if (cookies.get("authenticated")) {
      navigate('/cbt_creator/dashboard');
    }
  }, [navigate, cookies]);

  return (
    <>
      <Helmet>
        <meta id="description" name="description" content="cbthost sign up" />
        <title id="title">CBTHost | Sign Up</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <style>{`
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            background-color: #f9fafb;
          }
          .card-shadow {
            box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05);
          }
          .btn-primary {
            transition: all 0.2s ease;
          }
        `}</style>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden card-shadow">
          <div className="p-8">
            <div className="text-center">
              <a 
                href="https://cbthost.com" 
                className="text-2xl font-bold text-indigo-700 hover:text-indigo-800 transition-colors"
              >
                CBTHost
              </a>
              <h6 className="mt-4 text-3xl font-extrabold text-gray-900">Create your account</h6>
              <p className="mt-2 text-sm text-gray-600">
                Already have an account?{' '}
                <a href="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                  Sign in
                </a>
              </p>
            </div>

            <div className="mt-8">
              <div className="flex justify-center mb-6">
                <GoogleLogin
                  onSuccess={responseMessage}
                  onError={errorMessage}
                  theme="filled_blue"
                  size="large"
                  text="signup_with"
                  shape="rectangular"
                />
              </div>

              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or register with email</span>
                </div>
              </div>

              {processing && (
                <div className="mt-6 flex items-center justify-center text-indigo-600">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Creating your account</span>
                </div>
              )}

              <form className="mt-6 space-y-6" onSubmit={registerUser}>
                <div>
                  <label htmlFor="fullname" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1">
                    <input
                      id="fullname"
                      name="fullname"
                      type="text"
                      value={fullname}
                      onChange={(e) => setFullname(e.target.value)}
                      disabled={disabled_status}
                      className={`appearance-none block w-full px-3 py-3 border ${
                        formErrors.fullname ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                      placeholder="John Doe"
                    />
                    {formErrors.fullname && (
                      <p className="mt-2 text-sm text-red-600">{formErrors.fullname}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={disabled_status}
                      className={`appearance-none block w-full px-3 py-3 border ${
                        formErrors.email ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                      placeholder="you@example.com"
                    />
                    {formErrors.email && (
                      <p className="mt-2 text-sm text-red-600">{formErrors.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={disabled_status}
                      className={`appearance-none block w-full px-3 py-3 border ${
                        formErrors.password ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                      placeholder="••••••••"
                    />
                    {formErrors.password && (
                      <p className="mt-2 text-sm text-red-600">{formErrors.password}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={disabled_status}
                      className={`appearance-none block w-full px-3 py-3 border ${
                        formErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                      placeholder="••••••••"
                    />
                    {formErrors.confirmPassword && (
                      <p className="mt-2 text-sm text-red-600">{formErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <a href="/password-reset" className="font-medium text-indigo-600 hover:text-indigo-500">
                      Forgot your password?
                    </a>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={disabled_status}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed btn-primary"
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating Account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              By signing up, you agree to our <a href="https://cbthost.com/terms" className="text-indigo-600 hover:text-indigo-500">Terms of Service</a> and <a href="https://cbthost.com/privacy" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</a>
            </p>
            <p className="text-xs text-gray-500 text-center mt-2">
              © 2025 cbthost.com. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default SignUp;